import { computed, inject, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import transactionProvider, {
  initialValuesCashInProvider,
} from '@/modules/cashIn/config/cashInProvider';
import type { WompiStatusType, WompiStatusData } from '../views/WompiStatus/types/wompiStatus.type';
import type { WompiTransactionStatusResponse } from '../types/wompi.type';
import { currencyConfigFormat } from '@/helpers/formatCurrency';
import { cashInService } from '../services/cashIn.service';
import { WOMPI_SESSION_KEYS } from '../constants/wompi.constants';

/**
 * Composable para gestionar el estado de transacciones Wompi
 * Consulta el estado de la transacción desde múltiples fuentes (query params, sessionStorage)
 * e hidrata la vista de resultado con estados (loading, success, error)
 * @returns Objeto con estado de transacción, handlers de navegación y datos formateados
 */
export function useWompiStatus() {
  const router = useRouter();
  const { query } = useRoute();

  const transactionInject = inject(
    transactionProvider,
    initialValuesCashInProvider
  );

  const { transactionData } = transactionInject;

  const realStatus = ref<WompiStatusType | null>(null);
  const realTransactionData = ref<WompiTransactionStatusResponse | null>(null);
  const isLoadingStatus = ref(false);

  const getReferenceCode = (): string | null => {
    const sources = [
      query?.referenceCode as string,
      sessionStorage.getItem(WOMPI_SESSION_KEYS.REFERENCE_CODE),
      sessionStorage.getItem(WOMPI_SESSION_KEYS.PAYMENT_ID),
      query?.partnerOrderId as string,
      query?.paymentId as string,
    ];

    return sources.find((source) => source && source.trim() !== '') || null;
  };

  const fetchTransactionStatus = async (): Promise<void> => {
    isLoadingStatus.value = true;

    const referenceCode = getReferenceCode();
    
    if (!referenceCode) {
      realStatus.value = 'error';
      isLoadingStatus.value = false;
      return;
    }

    const result = await cashInService.getWompiTransactionStatus(referenceCode);

    result.fold(
      () => {
        realStatus.value = 'error';
        isLoadingStatus.value = false;
      },
      (success) => {
        const responseData = success.data as WompiTransactionStatusResponse;
        
        if (!responseData || Object.keys(responseData).length === 0) {
          realStatus.value = 'error';
          isLoadingStatus.value = false;
          return;
        }
        
        realTransactionData.value = success.data;
        
        if (responseData?.status) {
          const backendStatus = String(responseData.status).toUpperCase();
          
          if (backendStatus === 'APPROVED') {
            realStatus.value = 'success';
          } else {
            realStatus.value = 'error';
          }
        } else {
          realStatus.value = 'error';
        }

        isLoadingStatus.value = false;
      }
    );
  };

  onMounted(() => {
    fetchTransactionStatus();
  });

  const status = computed<WompiStatusType>(() => {
    if (isLoadingStatus.value) {
      return 'loading';
    }

    if (realStatus.value) {
      return realStatus.value;
    }

    return 'error';
  });

  const transactionNumber = computed(() => {
    if (!realTransactionData.value) {
      return '';
    }
    
    const responseData = realTransactionData.value;
    
    const backendTransactionId = 
      responseData?.reference ||
      responseData?.transactionId || 
      responseData?.transactionNumber || 
      responseData?.id ||
      responseData?.partnerOrderId ||
      responseData?.paymentId;
    
    return backendTransactionId ? String(backendTransactionId) : '';
  });

  const formattedAmount = computed(() => {
    if (!realTransactionData.value) {
      return '';
    }
    
    const responseData = realTransactionData.value;
    
    const backendAmount = responseData?.amount || responseData?.totalAmount || responseData?.value;
    const backendCurrency = responseData?.currency || responseData?.currencyCode || 'COP';
    
    if (!backendAmount) {
      return '';
    }
    
    const amount = Number(backendAmount);

    if (isNaN(amount)) {
      return '';
    }
    
    const formatted = currencyConfigFormat({
      amount,
      currencyCode: backendCurrency,
      hasCode: true,
      hasSymbol: true,
    });
    
    return formatted;
  });

  const statusData = computed<WompiStatusData>(() => ({
    status: status.value,
    transactionNumber: transactionNumber.value,
    amount: transactionData.value?.originAmount || 0,
    currency: transactionData.value?.originCurrency || '',
  }));

  function clearWompiSession() {
    sessionStorage.removeItem(WOMPI_SESSION_KEYS.REFERENCE_CODE);
    sessionStorage.removeItem(WOMPI_SESSION_KEYS.PAYMENT_ID);
    sessionStorage.removeItem(WOMPI_SESSION_KEYS.AMOUNT);
    sessionStorage.removeItem(WOMPI_SESSION_KEYS.CURRENCY);
  }

  function handleGoHome(): void {
    clearWompiSession();
    router.push({ name: 'movements' });
  }

  function handleTryAgain(): void {
    clearWompiSession();
    router.push({ name: 'recharge-money' });
  }

  return {
    status,
    transactionNumber,
    formattedAmount,
    statusData,
    realStatus,
    realTransactionData,
    isLoadingStatus,
    handleGoHome,
    handleTryAgain,
    fetchTransactionStatus,
  };
}
